import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movieList } from "@/mocks/movies";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function MovieDetail() {
  const { id } = useParams();
  const movie = movieList.find((item) => item.id === id);
  const [wantToWatch, setWantToWatch] = useState(false);

  useDocumentMeta(
    movie ? `${movie.title} - 影视推荐 - 色花堂` : "影视作品不存在 - 色花堂",
    movie?.synopsis,
  );

  if (!movie) {
    return (
      <div className="px-4 py-16 md:px-8">
        <div className="mx-auto w-full max-w-2xl">
          <EmptyState
            icon="ri-movie-line"
            title="没有找到这部影视作品"
            description="该作品可能已被移除或链接有误，返回影视推荐页看看其他内容吧。"
            actionLabel="返回影视推荐"
            actionTo="/movies"
          />
        </div>
      </div>
    );
  }

  const related = movieList
    .filter((item) => item.id !== movie.id && item.genre.some((g) => movie.genre.includes(g)))
    .slice(0, 3);

  return (
    <article>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视推荐", to: "/movies" },
              { label: movie.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
          <div className="mx-auto w-48 sm:w-56 lg:mx-0 lg:w-full">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-secondary-900">
              <img
                src={movie.poster}
                alt={movie.title}
                title={`${movie.title} - 色花堂影视推荐`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
              <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-sm font-bold text-background-50">
                <i className="ri-star-fill" aria-hidden="true"></i>
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="text-sm text-foreground-600">{movie.region}</span>
              <span className="text-sm text-foreground-600">{movie.year} 年</span>
              <span className="text-sm text-foreground-600">{movie.duration}</span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {movie.genre.map((g) => (
                <Link
                  key={g}
                  to={`/movies?genre=${encodeURIComponent(g)}`}
                  className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-200 cursor-pointer"
                >
                  {g}
                </Link>
              ))}
            </div>

            <div className="mt-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-base leading-loose text-foreground-800">{movie.synopsis}</p>
            </div>

            <div className="mt-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950">主要信息</h2>
              <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                  <i className="ri-user-star-line text-xl text-primary-600" aria-hidden="true"></i>
                  <div>
                    <dt className="text-xs text-foreground-500">导演</dt>
                    <dd className="text-sm font-medium text-foreground-900">{movie.director}</dd>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                  <i className="ri-time-line text-xl text-primary-600" aria-hidden="true"></i>
                  <div>
                    <dt className="text-xs text-foreground-500">时长</dt>
                    <dd className="text-sm font-medium text-foreground-900">{movie.duration}</dd>
                  </div>
                </div>
              </dl>
            </div>

            <div className="mt-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950">主要演员</h2>
              <ul className="mt-3 flex flex-wrap gap-3">
                {movie.cast.map((name) => (
                  <li
                    key={name}
                    className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className="ri-user-line text-sm" aria-hidden="true"></i>
                    </span>
                    <span className="text-sm text-foreground-800">{name}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setWantToWatch((prev) => !prev)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                  wantToWatch
                    ? "bg-primary-100 text-primary-700"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={wantToWatch ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} aria-hidden="true"></i>
                {wantToWatch ? "已加入想看" : "加入想看"}
              </button>
              <Link
                to="/movies"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
                返回影视推荐
              </Link>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8">
            <h2 className="mb-6 font-heading text-xl font-extrabold text-foreground-950">相似推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}